import React, { useCallback } from 'react';
import { Send, StopCircle } from 'lucide-react';
import { useTheme } from '../../../contexts/ThemeContext';

/**
 * Send/Cancel button component
 *
 * Displays:
 * - Send button when not streaming
 * - Stop button when streaming
 *
 * Uses onMouseDown instead of onClick to prevent iOS Safari from
 * swallowing the tap event when contentEditable loses focus.
 */

export interface SendButtonProps {
  isStreaming: boolean;
  canSend: boolean;
  onSend: () => void;
  onCancel: () => void;
}

const SendButton: React.FC<SendButtonProps> = ({ isStreaming, canSend, onSend, onCancel }) => {
  const { theme } = useTheme();

  const handleSend = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      e.preventDefault();
      if (canSend) {
        onSend();
      }
    },
    [canSend, onSend]
  );

  if (isStreaming) {
    return (
      <button
        type="button"
        onClick={onCancel}
        className={`p-1.5 rounded-md min-h-[44px] min-w-[44px] flex items-center justify-center ${theme.colors.text.secondary} hover:${theme.colors.text.primary} hover:${theme.colors.bg.secondary} transition-colors opacity-70 hover:opacity-100`}
        title="Detener generación"
        aria-label="Detener generación en curso"
      >
        <StopCircle className="w-4 h-4" />
      </button>
    );
  }

  return (
    <button
      type="button"
      onMouseDown={handleSend}
      disabled={!canSend}
      className={`p-2 rounded-lg min-h-[44px] min-w-[44px] flex items-center justify-center transition-all active:scale-95 ${
        canSend
          ? 'bg-nb-accent text-white hover:opacity-90'
          : `${theme.colors.text.secondary} opacity-40 cursor-not-allowed`
      }`}
      style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
      title="Enviar mensaje"
      aria-label="Enviar mensaje"
    >
      <Send className="w-5 h-5" style={{ transform: 'rotate(45deg)' }} />
    </button>
  );
};

export default SendButton;
